import React, {useState, useEffect} from 'react';
import { useSelector } from 'react-redux';
import CheckoutSubheader from "./CheckoutSubheader";
import CruisesOverview from "./CruisesOverview";
import Price from "./Price";
import {getCurrentCartFromStore, parseBookingParts, updateBookings, updateCart} from "../actions/cartActions";
import {getCurrentCruisesFromStore} from "../actions/cruisesActions";
import {logOutPassenger} from "../actions/authenticationActions";
import CartElementsConfirmationCombined from "./cartElements/CartElementsConfirmationCombined";
import BackLink from "./BackLink";
import ThemableSvgIcon from "./theme/ThemableSvgIcon"
import InfoBox from "./passengerInformation/InfoBox";
import { getExcursionsText, getExcursionText, getPaymentConfirmationThankYouText } from "../utils/themeUtils";
import {truncateStringLongerThan} from "../utils/miscUtils";
import { Plural, Trans, useLingui } from '@lingui/react/macro';

function PaymentConfirmationOverview(props) {
    const isAgency = useSelector(state => state.agency.isAuthenticated);

    const [cartSnapshot] = useState(getCurrentCartFromStore());
    const [cruisesSnapshot] = useState(getCurrentCruisesFromStore());

    // the order answer has one booking part per cart entry; when some failed, both lists come from the parts
    const failedParts = props.bookingParts?.filter(part => part.state === "Inventory_Failed") || [];
    const bookedParts = props.bookingParts?.filter(part => part.state !== "Inventory_Failed") || [];
    const failedCart = parseBookingParts(failedParts);
    const bookedCart = failedParts.length > 0 ? parseBookingParts(bookedParts) : cartSnapshot;
    // counted as the rows listed: an excursion booked for one guest but not another is a row in each list
    const nrBooked = bookedCart.nrExcursions;
    const nrItems = nrBooked + failedCart.nrExcursions;

    const {t} = useLingui();
    const excursionText = getExcursionText(true);
    const excursionName = getExcursionText();
    const excursionsName = getExcursionsText();

    useEffect(() => {
        window.scrollTo(0, 0);
        if(props.logoutUserOnMount) {
            logOutPassenger();
        }
        else {
            updateCart();
            updateBookings();
        }
    }, [props.logoutUserOnMount]);

    return (
        <div>
            <CheckoutSubheader
                active="confirmation"
                hideOnMobile={1}
            />

            <div className="container px-4 mx-auto mt-4 text-primary-dark">
                <div className="mb-4">
                    <div className="print-hidden">
                        <BackLink
                            to={isAgency ? t({ id: "/login" }) : t({ id: "/itinerary" })}
                            text={
                                isAgency
                                ? t({ id: "return_to_passenger_login" })
                                : t({ 
                                    id: "return_to_destination_excursion_planner",
                                    message: `Return to ${excursionText} Planner`
                                })
                            }
                        />
                    </div>
                </div>
                <div className="text-3xl mt-3 xl:mt-0 xl:inline">
                    {getPaymentConfirmationThankYouText()}
                </div>
            </div>


            <div className="container px-4 mx-auto">
                {/*booking reference box*/}
                <div className="bg-primary-lightest-25 p-4 mt-6 shadow">
                    <div className="text-primary-dark uppercase pr-2">
                        <Trans id="excursion_pre_registration_reference">{excursionText} Pre-Registration reference</Trans> {":"}
                        <span className="font-bold">{props.bookingNr}</span>
                    </div>
                </div>

                {props.includePrintLink &&
                    <div className="mt-4">
                        <button className="text-primary cursor-pointer print-hidden" onClick={() => {window.print()}}>
                            <Trans id="print_confirmation">Print confirmation</Trans>
                        </button>
                    </div>
                }

                {failedParts.length > 0 &&
                    <InfoBox
                        classes="mt-8"
                        icon="info-circle"
                        iconColorClass="text-red"
                        infoText={
                            <>
                                <div>
                                    <Plural
                                        id="x_of_y_excursions_booked"
                                        value={nrBooked}
                                        one={`# of ${nrItems} ${excursionsName} is booked`}
                                        other={`# of ${nrItems} ${excursionsName} are booked`}
                                    />
                                </div>
                                <div>
                                    <Plural
                                        id="x_excursions_not_booked_removed_from_cart"
                                        value={failedCart.nrExcursions}
                                        one={`# ${excursionName} could not be booked. It is listed below and has been removed from your cart and you will not be charged for it.`}
                                        other={`# ${excursionsName} could not be booked. They are listed below and have been removed from your cart and you will not be charged for them.`}
                                    />
                                </div>
                            </>
                        }
                    />
                }

                {/* excursions information */}
                {(failedParts.length === 0 || bookedParts.length > 0) &&
                    <div className="mt-8 px-2 sm:px-4">
                        <div className="border-b print-border-0 pb-2 sm:flex sm:justify-between sm:items-center ">
                            <h2><Trans id="your_destination_excursions">Your {getExcursionsText(true)}</Trans></h2>
                        </div>

                        <div className="text-primary-dark">
                            <CruisesOverview
                                cruisesSnapshot={cruisesSnapshot}
                                preText={`${t({id:"for"})} `}
                            />
                        </div>

                        <CartElementsConfirmationCombined
                            paidExcursions={bookedCart.excursions}
                            paidTacEntries={bookedCart.tac}
                            cruiseStartDate={cruisesSnapshot && cruisesSnapshot.cruiseStartDate}/>

                        <div className="text-right mb-4">
                            <Price
                                preText={t({id:"total_cost_is_added_to_onboard_account"})}
                                price={bookedCart.totalPrice}
                                currencySymbol={bookedCart.currencySymbol}
                            />
                        </div>
                    </div>
                }

                {/* entries the hub could not book */}
                {failedParts.length > 0 &&
                    <div className={`${bookedParts.length > 0 ? "mt-18" : "mt-8"} px-2 sm:px-4`}>
                        <div className="border-b print-border-0 pb-2">
                            <h2 className="text-red text-3xl"><Trans id="items_could_not_be_booked">The following items could NOT be booked</Trans></h2>
                        </div>

                        <div className="mt-4 text-grey-darker">
                            <Trans id="unable_to_book_following_excursions">Unfortunately, we were unable to book the following {getExcursionsText()}:</Trans>
                        </div>

                        <CartElementsConfirmationCombined
                            paidExcursions={failedCart.excursions}
                            paidTacEntries={failedCart.tac}
                            cruiseStartDate={cruisesSnapshot && cruisesSnapshot.cruiseStartDate}
                            strikePrices={true}/>

                        <div className="text-right">
                            <Price
                                preText={t({id: "total_cost_of_excursions_not_booked", message: `Total cost of ${excursionsName} not booked`})}
                                price={failedCart.totalPrice}
                                currencySymbol={failedCart.currencySymbol}
                                strikethrough={true}
                            />
                        </div>

                        <div className="mt-4 mb-4 text-grey-darker">
                            <Trans id="try_again_or_contact_call_centre">Please try again or contact the call centre for assistance.</Trans>
                        </div>
                    </div>
                }

            </div>
            {/*What happens next*/}
            <div className="bg-primary-lightest text-center pt-8 pb-4">
                <div className="container mx-auto text-primary-dark">
                    <div className="text-2xl uppercase px-4">
                        <Trans id="what_happens_next">What happens next?</Trans>
                    </div>

                    <div className="flex flex-wrap justify-center">
                        <Step title="Confirmation" imageName="at-symbol">
                            <div className="text-grey-darkest w-full">
                                <p><Trans id="you_will_receive_confirmation">You'll shortly receive a confirmation email at</Trans> {' '}
                                    <span className="hidden md:inline xl:hidden">
                                        {truncateStringLongerThan(props.confirmationMail, 30)}
                                    </span>
                                    <span className="md:hidden xl:inline">
                                        {truncateStringLongerThan(props.confirmationMail, 42)}
                                    </span>
                                </p>
                            </div>
                        </Step>


                        <Step title={t({id: "manage"}) + " " + getExcursionsText()} imageName="tablet">
                            <div className="text-grey-darkest">
                                <p><Trans id="login_to_manage_excursions">Log in to your account to manage your cruise {getExcursionsText()}</Trans></p>
                            </div>
                        </Step>
                    </div>
                </div>
            </div>

        </div>
    );
}

export default PaymentConfirmationOverview;

function Step(props) {
    return (
        <div className="w-full md:w-1/2 flex flex-col items-center py-4 px-4">
            <div className="w-30 h-30 bg-white rounded-full flex items-center justify-center">
                <ThemableSvgIcon className="h-16 w-16" alt="step logo" name={props.imageName}/>
            </div>
            <div className="uppercase mt-4">
                {props.title}
            </div>
            <div className="max-w-full mt-3 leading-normal">
                {props.children}
            </div>
        </div>
    )
}